// src/pages/elibrary/EditLibrary.tsx
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fireDB } from '../../../../Backend/Firebase/FirebaseConfigData';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  deleteDoc,
} from 'firebase/firestore';

interface LibraryEntry {
  title: string;
  author?: string;
  driveLink?: string;
  blogLink?: string;
  metadata?: string;
  dataType?: string;
}

interface Subtopic {
  id: string;
  title: string;
  author?: string;
  blogLink?: string;
  createdAt?: any;
  dataType?: string;
  driveLink?: string;
  fileType?: string;
  metadata?: string;
  pdfUrl?: string;
}

const EditLibrary = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<LibraryEntry | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [subtopics, setSubtopics] = useState<Subtopic[]>([]);
  const [subLoading, setSubLoading] = useState<boolean>(false);

  // Fetch main library entry
  useEffect(() => {
    const fetchEntry = async () => {
      try {
        if (!id) return;
        const docRef = doc(fireDB, 'libraryEntries', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setEntry(docSnap.data() as LibraryEntry);
        } else {
          alert('Entry not found.');
          navigate('/elibrary/update');
        }
      } catch (error) {
        console.error('Error fetching entry:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEntry();
  }, [id, navigate]);

  // Fetch subtopics related to this entry
  useEffect(() => {
    const fetchSubtopics = async () => {
      if (!id) return;
      try {
        setSubLoading(true);
        const subRef = collection(fireDB, 'libraryEntries', id, 'subtopics');
        const querySnapshot = await getDocs(subRef);
        const data: Subtopic[] = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Subtopic),
        }));
        setSubtopics(data);
      } catch (error) {
        console.error('Error fetching subtopics:', error);
      } finally {
        setSubLoading(false);
      }
    };

    fetchSubtopics();
  }, [id]);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entry || !id) return;
    try {
      await updateDoc(doc(fireDB, 'libraryEntries', id), entry);
      alert('Library entry updated successfully!');
      navigate('/elibrary/update');
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };

  const handleDeleteSubtopic = async (subId: string) => {
    if (!id || !window.confirm('Are you sure you want to delete this subtopic?')) return;
    try {
      await deleteDoc(doc(fireDB, 'libraryEntries', id, 'subtopics', subId));
      setSubtopics((prev) => prev.filter((sub) => sub.id !== subId));
    } catch (error) {
      console.error('Error deleting subtopic:', error);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Loading entry...</p>
      </div>
    );

  if (!entry)
    return (
      <div className="text-center mt-20 text-gray-600">
        <p>Entry not found.</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <form
        onSubmit={handleUpdate}
        className="bg-white shadow-md rounded-lg p-6 max-w-2xl mx-auto"
      >
        <h2 className="text-2xl font-semibold mb-4 text-center">✏️ Edit Library Entry</h2>

        <label className="block mb-2 font-medium">Title</label>
        <input
          type="text"
          value={entry.title}
          onChange={(e) => setEntry({ ...entry, title: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
          required
        />

        <label className="block mb-2 font-medium">Author</label>
        <input
          type="text"
          value={entry.author || ''}
          onChange={(e) => setEntry({ ...entry, author: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Drive Link</label>
        <input
          type="url"
          value={entry.driveLink || ''}
          onChange={(e) => setEntry({ ...entry, driveLink: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Blog Link</label>
        <input
          type="url"
          value={entry.blogLink || ''}
          onChange={(e) => setEntry({ ...entry, blogLink: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Metadata</label>
        <textarea
          value={entry.metadata || ''}
          onChange={(e) => setEntry({ ...entry, metadata: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Type</label>
        <select
          value={entry.dataType || 'notes'}
          onChange={(e) => setEntry({ ...entry, dataType: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        >
          <option value="notes">Notes</option>
          <option value="pyqs">PYQs</option>
          <option value="blog">Blog</option>
          <option value="tutorial">Tutorial</option>
        </select>

        <button
          type="submit"
          className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 transition"
        >
          Update Entry
        </button>
      </form>
{/* Add Subheading Button */}
<div className="flex max-w-6xl content-center  mx-auto mt-6 p-5">
  <button
    onClick={() => navigate(`/elibrary/${id}/subtopic/new`)}
    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
  >
    ➕ Add New Entry
  </button>
</div>

      {/* Subtopics Table */}
      <div className="mt-12 max-w-6xl mx-auto bg-white p-6 rounded-lg shadow-md overflow-x-auto">
        <h3 className="text-xl font-semibold mb-4 text-gray-800">
          📘 All Subtopics under this Entry
        </h3>

        {subLoading ? (
          <p>Loading subtopics...</p>
        ) : subtopics.length === 0 ? (
          <p className="text-gray-500">No subtopics found for this entry.</p>
        ) : (
          <table className="w-full border border-gray-200 text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="py-2 px-3">Title</th>
                <th className="py-2 px-3">Author</th>
                <th className="py-2 px-3">Drive Link</th>
                <th className="py-2 px-3">Blog Link</th>
                <th className="py-2 px-3">File Type</th>
                <th className="py-2 px-3">Data Type</th>
                <th className="py-2 px-3">Metadata</th>
                <th className="py-2 px-3">Created At</th>
                <th className="py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subtopics.map((sub) => (
                <tr key={sub.id} className="border-t hover:bg-gray-50">
                  <td className="py-2 px-3">{sub.title}</td>
                  <td className="py-2 px-3">{sub.author || '-'}</td>
                  <td className="py-2 px-3">
                    {sub.driveLink ? (
                      <a
                        href={sub.driveLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        Drive
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-2 px-3">
                    {sub.blogLink ? (
                      <a
                        href={sub.blogLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        Blog
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-2 px-3">{sub.fileType || '-'}</td>
                  <td className="py-2 px-3">{sub.dataType || '-'}</td>
                  <td className="py-2 px-3">{sub.metadata || '-'}</td>
                  <td className="py-2 px-3">
                    {sub.createdAt?.toDate
                      ? sub.createdAt.toDate().toLocaleString()
                      : '-'}
                  </td>
                  <td className="py-2 px-3 text-center space-x-3">
                    <button
                      onClick={() => navigate(`/elibrary/${id}/subtopic/${sub.id}/edit`)}
                      className="text-blue-600 hover:underline"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => handleDeleteSubtopic(sub.id)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EditLibrary;
